import { useEffect, useState } from 'react'
import api from '@/lib/api'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import { useBrand, splitBrandName } from '@/hooks/useBrand'
import { PageHeader } from '@/components/dashboard-ui'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Loader2, Plus, Trash2, ArrowLeft, Copy, Check, ExternalLink, Lock, X } from 'lucide-react'

interface ModuleParam {
  key: string
  label: string
  type: string
  default: number | string
  hint?: string
}
interface CatalogModule {
  id: string
  name: string
  category: string
  billing: string
  defaultPriceCents: number
  minPriceCents: number
  managementFeePct?: number
  description: string
  params: ModuleParam[]
  termOptions: string[]
  defaultTerm: string
}
interface DealItem {
  id: string
  moduleId: string
  name: string
  category: string
  billing: string
  unitPriceCents: number
  quantity: number
  term: string
  managementFeePct: number | null
  params: Record<string, unknown>
  position: number
}
interface Deal {
  id: string
  token: string
  status: string
  organizationId: string | null
  prospectBusiness: string | null
  prospectContactName: string | null
  prospectEmail: string | null
  prospectAddress: string | null
  affiliateId: string | null
  affiliateName: string | null
  monthlyTotalCents: number
  oneTimeTotalCents: number
  notes: string | null
  items: DealItem[]
  createdAt: string
}
interface DealTask {
  id: string
  moduleId: string
  moduleName: string
  title: string
  status: string
  position: number
}
interface DealAccess {
  level: 'super' | 'trusted' | 'builder'
  maxDiscountPct: number | null
}

const TERM_LABEL: Record<string, string> = {
  monthly: 'Monthly',
  '3_month': '3-month term',
  '6_month': '6-month term',
  '12_month': '12-month term',
  one_time: 'One-time',
}
const CATEGORY_LABEL: Record<string, string> = {
  plan: 'Plans',
  seo: 'SEO',
  website: 'Website builds',
  migration: 'Migration',
  ad: 'Advertising',
  one_time: 'One-time fees',
}
const CATEGORY_ORDER = ['plan', 'seo', 'website', 'migration', 'ad', 'one_time']
const STATUS_VARIANT: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  draft: 'outline',
  sent: 'secondary',
  viewed: 'secondary',
  signed: 'default',
  paid: 'default',
  provisioned: 'default',
  cancelled: 'destructive',
}

// A deal can be deleted only before it goes live — once paid/provisioned it
// owns a Stripe subscription and a workspace, and a signed deal is a binding
// contract record. Those are cancelled, not deleted (mirrors the API guard).
const DELETABLE_STATUSES = new Set(['draft', 'sent', 'viewed', 'cancelled'])

function fmt(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`
}

export default function DealBuilderPage() {
  const { toast } = useToast()
  const brand = useBrand()
  const brandBaseName = splitBrandName(brand.appName)[0]
  const [mode, setMode] = useState<'list' | 'builder'>('list')
  const [deals, setDeals] = useState<Deal[]>([])
  const [catalog, setCatalog] = useState<CatalogModule[]>([])
  const [current, setCurrent] = useState<Deal | null>(null)
  const [access, setAccess] = useState<DealAccess | null>(null)
  const [accessDenied, setAccessDenied] = useState(false)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [copied, setCopied] = useState(false)
  const [pBusiness, setPBusiness] = useState('')
  const [pContact, setPContact] = useState('')
  const [pEmail, setPEmail] = useState('')
  const [pAddress, setPAddress] = useState('')
  const [affiliateCode, setAffiliateCode] = useState('')
  const [tasks, setTasks] = useState<DealTask[]>([])
  const [pendingDelete, setPendingDelete] = useState<{ deal: Deal; returnToList: boolean } | null>(null)

  useEffect(() => {
    loadAccess()
    loadList()
    loadCatalog()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fail = (title: string, err: any) =>
    toast({ title, description: err?.response?.data?.message ?? err?.message, variant: 'destructive' })

  const loadAccess = async () => {
    try {
      const res = await api.get('/api/deals/access')
      setAccess(res.data.data)
    } catch (err: any) {
      if (err?.response?.status === 403) setAccessDenied(true)
    }
  }

  const loadList = async () => {
    setLoading(true)
    try {
      const res = await api.get('/api/deals')
      setDeals(res.data.data.deals)
    } catch (err: any) {
      if (err?.response?.status === 403) setAccessDenied(true)
      else fail('Failed to load deals', err)
    } finally {
      setLoading(false)
    }
  }

  const loadCatalog = async () => {
    try {
      const res = await api.get('/api/deals/catalog')
      setCatalog(res.data.data.modules)
    } catch (err: any) {
      if (err?.response?.status !== 403) fail('Failed to load module catalog', err)
    }
  }

  const setProspectFields = (d: Deal) => {
    setPBusiness(d.prospectBusiness ?? '')
    setPContact(d.prospectContactName ?? '')
    setPEmail(d.prospectEmail ?? '')
    setPAddress(d.prospectAddress ?? '')
  }

  const openDeal = async (id: string) => {
    setBusy(true)
    try {
      const res = await api.get(`/api/deals/${id}`)
      const d: Deal = res.data.data.deal
      setCurrent(d)
      setProspectFields(d)
      setCopied(false)
      const taskRes = await api.get(`/api/deals/${id}/tasks`)
      setTasks(taskRes.data.data.tasks)
      setMode('builder')
    } catch (err: any) {
      fail('Failed to open deal', err)
    } finally {
      setBusy(false)
    }
  }

  const newDeal = async () => {
    setBusy(true)
    try {
      const res = await api.post('/api/deals', {})
      const d: Deal = res.data.data.deal
      setCurrent(d)
      setProspectFields(d)
      setTasks([])
      setCopied(false)
      setMode('builder')
    } catch (err: any) {
      fail('Failed to create deal', err)
    } finally {
      setBusy(false)
    }
  }

  const backToList = async () => {
    setCurrent(null)
    setTasks([])
    setMode('list')
    await loadList()
  }

  const saveProspect = async () => {
    if (!current) return
    setBusy(true)
    try {
      const res = await api.put(`/api/deals/${current.id}`, {
        prospectBusiness: pBusiness || null,
        prospectContactName: pContact || null,
        prospectEmail: pEmail || null,
        prospectAddress: pAddress || null,
      })
      setCurrent(res.data.data.deal)
      toast({ title: 'Prospect details saved' })
    } catch (err: any) {
      fail('Save failed', err)
    } finally {
      setBusy(false)
    }
  }

  const markSent = async () => {
    if (!current) return
    setBusy(true)
    try {
      const res = await api.put(`/api/deals/${current.id}`, { status: 'sent' })
      setCurrent(res.data.data.deal)
      toast({ title: 'Marked as sent', description: 'The deal now reads as sent in your list.' })
    } catch (err: any) {
      fail('Could not update status', err)
    } finally {
      setBusy(false)
    }
  }

  const attachAffiliate = async () => {
    if (!current || !affiliateCode.trim()) return
    setBusy(true)
    try {
      const lookup = await api.get('/api/deals/affiliate-by-code', {
        params: { code: affiliateCode.trim() },
      })
      const { affiliateId, displayName } = lookup.data.data
      const res = await api.put(`/api/deals/${current.id}`, { affiliateId })
      setCurrent(res.data.data.deal)
      setAffiliateCode('')
      toast({
        title: 'Affiliate credited',
        description: `${displayName} earns commission once this deal is paid.`,
      })
    } catch (err: any) {
      fail('Could not credit that affiliate', err)
    } finally {
      setBusy(false)
    }
  }

  const removeAffiliate = async () => {
    if (!current) return
    setBusy(true)
    try {
      const res = await api.put(`/api/deals/${current.id}`, { affiliateId: null })
      setCurrent(res.data.data.deal)
      toast({ title: 'Affiliate credit removed' })
    } catch (err: any) {
      fail('Could not update affiliate credit', err)
    } finally {
      setBusy(false)
    }
  }

  const copyContractLink = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
      toast({ title: 'Contract link copied', description: 'Send it to the prospect to sign and pay.' })
    } catch {
      toast({ title: 'Copy failed', description: 'Select the link and copy it manually.', variant: 'destructive' })
    }
  }

  const addModule = async (m: CatalogModule) => {
    if (!current) return
    setBusy(true)
    try {
      const params = Object.fromEntries(m.params.map((p) => [p.key, p.default]))
      const res = await api.post(`/api/deals/${current.id}/items`, { moduleId: m.id, params })
      setCurrent(res.data.data.deal)
    } catch (err: any) {
      fail('Could not add module', err)
    } finally {
      setBusy(false)
    }
  }

  const updateItem = async (itemId: string, patch: Record<string, unknown>) => {
    if (!current) return
    try {
      const res = await api.put(`/api/deals/${current.id}/items/${itemId}`, patch)
      setCurrent(res.data.data.deal)
    } catch (err: any) {
      fail('Could not update line item', err)
    }
  }

  const removeItem = async (itemId: string) => {
    if (!current) return
    try {
      const res = await api.delete(`/api/deals/${current.id}/items/${itemId}`)
      setCurrent(res.data.data.deal)
    } catch (err: any) {
      fail('Could not remove line item', err)
    }
  }

  // Open the branded confirm modal. The actual delete runs in confirmDelete.
  const requestDelete = (deal: Deal, returnToList = false) => {
    setPendingDelete({ deal, returnToList })
  }

  const confirmDelete = async () => {
    if (!pendingDelete) return
    const { deal: d, returnToList } = pendingDelete
    const label = d.prospectBusiness || 'Untitled deal'
    setBusy(true)
    try {
      await api.delete(`/api/deals/${d.id}`)
      setDeals((prev) => prev.filter((x) => x.id !== d.id))
      toast({ title: 'Deal deleted', description: `“${label}” has been removed.` })
      if (returnToList) {
        setCurrent(null)
        setTasks([])
        setMode('list')
        await loadList()
      }
    } catch (err: any) {
      fail('Could not delete deal', err)
    } finally {
      setBusy(false)
      setPendingDelete(null)
    }
  }

  const toggleTask = async (task: DealTask) => {
    if (!current) return
    const next = task.status === 'done' ? 'pending' : 'done'
    try {
      const res = await api.put(`/api/deals/${current.id}/tasks/${task.id}`, { status: next })
      setTasks((prev) => prev.map((t) => (t.id === task.id ? res.data.data.task : t)))
    } catch (err: any) {
      fail('Could not update task', err)
    }
  }

  /**
   * Whether the price field is locked for this line. Super admins always edit.
   * Ad-budget (pass-through) lines are a client-chosen amount, so anyone edits
   * them. Migration is page-banded — affiliates can't override it. Builder-tier
   * affiliates can't discount anything else; trusted affiliates can.
   */
  const priceLocked = (item: DealItem): boolean => {
    if (!access || access.level === 'super') return false
    if (item.billing === 'pass_through') return false
    if (item.category === 'migration') return true
    return access.level === 'builder'
  }

  const isSuper = access?.level === 'super'
  const eyebrow = isSuper ? 'Platform Admin' : 'Partner Tools'
  const lineItemsHint =
    access?.level === 'trusted'
      ? `Adjust price (down to ${access.maxDiscountPct ?? 20}% off standard), term, and options.`
      : access?.level === 'builder'
        ? `Set terms and options. Pricing follows the standard ${brandBaseName} catalog.`
        : 'Adjust price, term, and options per module.'

  // Branded delete confirmation — replaces the native window.confirm box.
  // Rendered in both the list and builder views so either delete affordance
  // opens the same modal.
  const deleteDialog = (
    <Dialog
      open={!!pendingDelete}
      onOpenChange={(open) => {
        if (!open && !busy) setPendingDelete(null)
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-destructive/10 text-destructive">
              <Trash2 className="h-4 w-4" />
            </div>
            <DialogTitle>Delete this deal?</DialogTitle>
          </div>
          <DialogDescription className="pt-1">
            <span className="font-medium text-foreground">
              {pendingDelete?.deal.prospectBusiness || 'Untitled deal'}
            </span>{' '}
            and its line items will be permanently removed. This can&rsquo;t be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => setPendingDelete(null)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={confirmDelete} disabled={busy}>
            {busy ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4 mr-2" />
            )}
            Delete deal
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )

  // ── Access-denied state ─────────────────────────────────────────────────────
  if (accessDenied) {
    return (
      <div className="space-y-5 sm:space-y-6">
        <PageHeader
          eyebrow="Deal Builder"
          title="Deal Builder"
          subtitle="Assemble custom deals and send a contract for signature."
        />
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            Deal Builder access isn&rsquo;t enabled for your account. Ask {brandBaseName} to turn it on.
          </CardContent>
        </Card>
      </div>
    )
  }

  // ── List view ──────────────────────────────────────────────────────────────
  if (mode === 'list') {
    return (
      <div className="space-y-5 sm:space-y-6">
        <PageHeader
          eyebrow={eyebrow}
          title="Deal Builder"
          subtitle="Assemble custom deals from modules, then send a contract for signature."
        />
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0">
            <div className="space-y-1">
              <CardTitle className="text-base">Custom deals</CardTitle>
              <CardDescription>Every custom deal, newest first.</CardDescription>
            </div>
            <Button onClick={newDeal} disabled={busy}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              New deal
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                Loading deals…
              </div>
            ) : deals.length === 0 ? (
              <div className="py-12 text-center text-sm text-muted-foreground">
                No deals yet. Click &ldquo;New deal&rdquo; to build one.
              </div>
            ) : (
              <div className="divide-y">
                {deals.map((d) => (
                  <div
                    key={d.id}
                    className="flex items-center transition-colors hover:bg-muted/40"
                  >
                    <button
                      onClick={() => openDeal(d.id)}
                      className="flex min-w-0 flex-1 items-center justify-between gap-4 px-4 py-3 text-left"
                    >
                      <div className="min-w-0">
                        <p className="font-medium truncate">
                          {d.prospectBusiness || 'Untitled deal'}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {d.prospectContactName || d.prospectEmail || 'No contact yet'} ·{' '}
                          {new Date(d.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-4 shrink-0">
                        <div className="text-right">
                          <p className="text-sm font-mono">{fmt(d.monthlyTotalCents)}/mo</p>
                          <p className="text-xs text-muted-foreground font-mono">
                            {fmt(d.oneTimeTotalCents)} upfront
                          </p>
                        </div>
                        <Badge variant={STATUS_VARIANT[d.status] ?? 'outline'} className="capitalize">
                          {d.status}
                        </Badge>
                      </div>
                    </button>
                    {DELETABLE_STATUSES.has(d.status) && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="mr-2 shrink-0 text-muted-foreground hover:text-destructive"
                        aria-label={`Delete deal for ${d.prospectBusiness || 'untitled deal'}`}
                        disabled={busy}
                        onClick={() => requestDelete(d)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        {deleteDialog}
      </div>
    )
  }

  // ── Builder view ───────────────────────────────────────────────────────────
  if (!current) return null

  const dueNowCents = current.monthlyTotalCents + current.oneTimeTotalCents
  const contractUrl = `${window.location.origin}/deal/${current.token}`
  const modulesByCategory = CATEGORY_ORDER.map((cat) => ({
    cat,
    modules: catalog.filter((m) => m.category === cat),
  })).filter((g) => g.modules.length > 0)
  const doneCount = tasks.filter((t) => t.status === 'done').length
  const tasksByModule: { moduleName: string; tasks: DealTask[] }[] = []
  for (const t of tasks) {
    let group = tasksByModule.find((g) => g.moduleName === t.moduleName)
    if (!group) {
      group = { moduleName: t.moduleName, tasks: [] }
      tasksByModule.push(group)
    }
    group.tasks.push(t)
  }

  return (
    <div className="space-y-5 sm:space-y-6">
      <button
        onClick={backToList}
        className="flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        All deals
      </button>

      <PageHeader
        eyebrow="Deal Builder"
        title={current.prospectBusiness || 'New custom deal'}
        subtitle="Add modules, set pricing and terms, then send the contract."
      />

      {/* Prospect details */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Who is this for?</CardTitle>
            <Badge variant={STATUS_VARIANT[current.status] ?? 'outline'} className="capitalize">
              {current.status}
            </Badge>
          </div>
          <CardDescription>
            Enter the prospect&rsquo;s details. The organization is created automatically once the deal is paid.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-[11px] text-muted-foreground uppercase tracking-wider block mb-1">
                Business name
              </label>
              <Input value={pBusiness} onChange={(e) => setPBusiness(e.target.value)} placeholder="Joe Bloggs Law PLLC" />
            </div>
            <div>
              <label className="text-[11px] text-muted-foreground uppercase tracking-wider block mb-1">
                Contact name
              </label>
              <Input value={pContact} onChange={(e) => setPContact(e.target.value)} placeholder="Joe Bloggs" />
            </div>
            <div>
              <label className="text-[11px] text-muted-foreground uppercase tracking-wider block mb-1">
                Contact email
              </label>
              <Input value={pEmail} onChange={(e) => setPEmail(e.target.value)} placeholder="joe@bloggslaw.com" />
            </div>
          </div>
          <div>
            <label className="text-[11px] text-muted-foreground uppercase tracking-wider block mb-1">
              Business address{' '}
              <span className="normal-case tracking-normal text-muted-foreground/60">
                (optional — shown on the contract)
              </span>
            </label>
            <Input
              value={pAddress}
              onChange={(e) => setPAddress(e.target.value)}
              placeholder="123 Main St, Suite 200, San Diego, CA 92101"
            />
          </div>
          <div className="flex items-center justify-between">
            {DELETABLE_STATUSES.has(current.status) ? (
              <Button
                size="sm"
                variant="ghost"
                className="text-muted-foreground hover:text-destructive"
                onClick={() => requestDelete(current, true)}
                disabled={busy}
              >
                <Trash2 className="h-4 w-4 mr-1.5" />
                Delete deal
              </Button>
            ) : (
              <span />
            )}
            <Button size="sm" variant="outline" onClick={saveProspect} disabled={busy}>
              Save details
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Affiliate credit — who earns commission when this deal is paid */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Affiliate credit</CardTitle>
          <CardDescription>
            {isSuper
              ? 'Optionally credit an affiliate by their referral code — they earn commission on every invoice once the deal is paid.'
              : 'Deals you build are credited to your affiliate account. You earn commission on every invoice once the deal is paid.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {current.affiliateName ? (
            <div className="flex items-center justify-between gap-3 rounded-lg border p-3">
              <div className="flex items-center gap-2 min-w-0">
                <Badge variant="secondary">Credited</Badge>
                <span className="text-sm font-medium truncate">{current.affiliateName}</span>
                {!isSuper && <span className="text-xs text-muted-foreground">(you)</span>}
              </div>
              {isSuper && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-muted-foreground hover:text-destructive"
                  aria-label="Remove affiliate credit"
                  onClick={removeAffiliate}
                  disabled={busy}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          ) : isSuper ? (
            <div className="flex items-center gap-2">
              <Input
                value={affiliateCode}
                onChange={(e) => setAffiliateCode(e.target.value)}
                placeholder="Affiliate code (e.g. JANE-DOE-1A2B)"
                className="font-mono text-sm"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') attachAffiliate()
                }}
              />
              <Button
                size="sm"
                variant="outline"
                className="shrink-0"
                onClick={attachAffiliate}
                disabled={busy || !affiliateCode.trim()}
              >
                Credit affiliate
              </Button>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              No affiliate is credited on this deal yet.
            </p>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-6">
        {/* Catalog */}
        <Card className="h-fit">
          <CardHeader>
            <CardTitle className="text-base">Add modules</CardTitle>
            <CardDescription>Click a module to add it to the deal. Each line spells out what it includes.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {modulesByCategory.map((group) => (
              <div key={group.cat}>
                <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider mb-1.5">
                  {CATEGORY_LABEL[group.cat] ?? group.cat}
                </p>
                <div className="space-y-1.5">
                  {group.modules.map((m) => (
                    <div
                      key={m.id}
                      className="flex items-start justify-between gap-3 rounded-lg border p-2.5"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium">{m.name}</p>
                        <p className="text-xs text-muted-foreground font-mono">
                          {fmt(m.defaultPriceCents)}
                          {m.billing === 'recurring' || m.billing === 'pass_through' ? '/mo' : ''}
                          {m.billing === 'pass_through' && m.managementFeePct
                            ? ` + ${m.managementFeePct}% mgmt`
                            : ''}
                        </p>
                        <p className="text-[11px] text-muted-foreground leading-snug mt-1">
                          {m.description}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="shrink-0"
                        disabled={busy}
                        aria-label={`Add ${m.name}`}
                        onClick={() => addModule(m)}
                      >
                        <Plus className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Line items + totals */}
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Line items</CardTitle>
              <CardDescription>{lineItemsHint}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {current.items.length === 0 && (
                <p className="text-sm text-muted-foreground py-4 text-center">
                  No modules yet — add some from the left.
                </p>
              )}
              {current.items.map((item) => {
                const module = catalog.find((m) => m.id === item.moduleId)
                const termOptions = module?.termOptions ?? [item.term]
                const isAdBudget = item.billing === 'pass_through'
                const locked = priceLocked(item)
                const feeCents = item.managementFeePct
                  ? Math.round(item.unitPriceCents * item.quantity * (item.managementFeePct / 100))
                  : 0
                return (
                  <div key={item.id} className="rounded-lg border p-3 space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium">{item.name}</p>
                        <p className="text-[11px] text-muted-foreground capitalize">
                          {CATEGORY_LABEL[item.category] ?? item.category} ·{' '}
                          {item.billing === 'pass_through' ? 'pass-through' : item.billing.replace('_', '-')}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-muted-foreground hover:text-destructive"
                        aria-label={`Remove ${item.name}`}
                        onClick={() => removeItem(item.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>

                    {/* What this module includes */}
                    {module?.description && (
                      <p className="text-[11px] text-muted-foreground leading-snug">
                        {module.description}
                      </p>
                    )}

                    {/* Module parameters */}
                    {(module?.params ?? []).length > 0 && (
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {module!.params.map((p) => (
                          <div key={p.key}>
                            <label className="text-[10px] text-muted-foreground uppercase tracking-wider block mb-0.5">
                              {p.label}
                            </label>
                            <Input
                              type={p.type === 'number' ? 'number' : 'text'}
                              defaultValue={String(item.params?.[p.key] ?? p.default)}
                              className="h-8 text-sm"
                              onBlur={(e) => {
                                const raw = e.target.value
                                const val = p.type === 'number' ? Number(raw) : raw
                                updateItem(item.id, { params: { ...item.params, [p.key]: val } })
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="flex flex-wrap items-end gap-2">
                      <div className="w-36">
                        <label className="text-[10px] text-muted-foreground uppercase tracking-wider block mb-0.5">
                          {isAdBudget ? 'Monthly ad budget ($)' : 'Price ($)'}
                        </label>
                        <Input
                          // Re-keyed by price so backend-driven changes (banded
                          // migration price, builder-tier catalog snap) show.
                          key={`price-${item.id}-${item.unitPriceCents}`}
                          type="number"
                          step="0.01"
                          min="0"
                          disabled={locked}
                          defaultValue={(item.unitPriceCents / 100).toString()}
                          className="h-8 text-sm"
                          onBlur={(e) => {
                            const cents = Math.round(parseFloat(e.target.value) * 100)
                            if (!Number.isNaN(cents) && cents !== item.unitPriceCents) {
                              updateItem(item.id, { unitPriceCents: cents })
                            }
                          }}
                        />
                      </div>
                      <div className="w-20">
                        <label className="text-[10px] text-muted-foreground uppercase tracking-wider block mb-0.5">
                          Qty
                        </label>
                        <Input
                          type="number"
                          min="1"
                          defaultValue={String(item.quantity)}
                          className="h-8 text-sm"
                          onBlur={(e) => {
                            const q = parseInt(e.target.value, 10)
                            if (!Number.isNaN(q) && q >= 1 && q !== item.quantity) {
                              updateItem(item.id, { quantity: q })
                            }
                          }}
                        />
                      </div>
                      {termOptions.length > 1 && (
                        <div className="min-w-[150px]">
                          <label className="text-[10px] text-muted-foreground uppercase tracking-wider block mb-0.5">
                            Term
                          </label>
                          <Select
                            value={item.term}
                            onValueChange={(v) => updateItem(item.id, { term: v })}
                          >
                            <SelectTrigger className="h-8 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {termOptions.map((t) => (
                                <SelectItem key={t} value={t}>
                                  {TERM_LABEL[t] ?? t}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>

                    {locked && (
                      <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                        <Lock className="h-3 w-3" />
                        {item.category === 'migration'
                          ? 'Migration is priced by page count — set the page count above.'
                          : `Priced at the standard ${brandBaseName} rate.`}
                      </p>
                    )}

                    {item.managementFeePct ? (
                      <p className="text-[11px] text-muted-foreground">
                        + {item.managementFeePct}% management fee ({fmt(feeCents)}/mo) ·{' '}
                        <span className="font-medium text-foreground">
                          {fmt(item.unitPriceCents * item.quantity + feeCents)}/mo billed
                        </span>
                      </p>
                    ) : null}
                  </div>
                )
              })}
            </CardContent>
          </Card>

          {/* Totals */}
          <Card className="card-inset">
            <CardContent className="py-4 space-y-1.5">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Recurring</span>
                <span className="font-mono font-medium">{fmt(current.monthlyTotalCents)}/mo</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">One-time (upfront)</span>
                <span className="font-mono font-medium">{fmt(current.oneTimeTotalCents)}</span>
              </div>
              <div className="flex justify-between text-base pt-1.5 border-t">
                <span className="font-medium">Due today</span>
                <span className="font-mono font-semibold">{fmt(dueNowCents)}</span>
              </div>
            </CardContent>
          </Card>

          {/* Contract link — share with the prospect to sign + pay */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Send the contract</CardTitle>
              <CardDescription>
                Share this link with {current.prospectContactName || 'the prospect'}. They review
                the agreement, sign, and pay — the workspace is created automatically.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-2">
                <Input
                  readOnly
                  value={contractUrl}
                  onFocus={(e) => e.currentTarget.select()}
                  className="font-mono text-xs"
                />
                <Button
                  size="sm"
                  variant="outline"
                  className="shrink-0"
                  onClick={() => copyContractLink(contractUrl)}
                >
                  {copied ? (
                    <>
                      <Check className="h-4 w-4 mr-1.5" /> Copied
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4 mr-1.5" /> Copy
                    </>
                  )}
                </Button>
              </div>
              <div className="flex items-center justify-between gap-3">
                <a
                  href={contractUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  Preview contract page
                </a>
                {current.status === 'draft' && (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy || current.items.length === 0}
                    onClick={markSent}
                  >
                    Mark as sent
                  </Button>
                )}
              </div>
              {current.items.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  Add at least one module before sending — an empty contract can&rsquo;t be signed.
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Fulfillment checklist — appears once the deal is paid & provisioned */}
      {tasks.length > 0 && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0">
            <div className="space-y-1">
              <CardTitle className="text-base">Work checklist</CardTitle>
              <CardDescription>
                Fulfillment tasks for this client — tick them off as you go.
              </CardDescription>
            </div>
            <span className="text-sm font-mono text-muted-foreground shrink-0">
              {doneCount}/{tasks.length} done
            </span>
          </CardHeader>
          <CardContent className="space-y-4">
            {tasksByModule.map((group) => (
              <div key={group.moduleName}>
                <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider mb-1.5">
                  {group.moduleName}
                </p>
                <div className="space-y-0.5">
                  {group.tasks.map((t) => (
                    <label
                      key={t.id}
                      className="flex items-start gap-2.5 text-sm cursor-pointer rounded-md px-2 py-1.5 transition-colors hover:bg-muted/40"
                    >
                      <input
                        type="checkbox"
                        checked={t.status === 'done'}
                        onChange={() => toggleTask(t)}
                        className="h-4 w-4 mt-0.5 rounded border-input accent-brand-royal"
                      />
                      <span className={t.status === 'done' ? 'line-through text-muted-foreground' : ''}>
                        {t.title}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
      {deleteDialog}
    </div>
  )
}
